import { dateFilterParams, formatLongPrice, formatPercentage, headerInfoTooltipDef } from '../lib/utils';
import { DateTime } from 'luxon';
import type { ColDef, ColGroupDef, KeyCreatorParams } from 'ag-grid-community';
import React from 'react';
import { FDA } from '@benzinga/calendar-manager';

const defaultFilterParams = {
  newRowsAction: 'keep',
};

const CompanyCell = ({ data }: { data: FDA }) => {
  if (!data?.companies || data.companies.length === 0) return <span>-</span>;

  return (
    <div className="flex flex-col gap-1.5 w-full">
      {data.companies.map((company, index: number) => {
        const logo = company.securities[0]?.logo;
        const ticker = company.securities[0]?.symbol;

        if (!company.securities || company.securities.length === 0) return null;

        return (
          <div className="flex items-center gap-2.5" key={`${ticker}-${index}`}>
            {logo && (
              <div className="shrink-0">
                <img alt={ticker || company.name} className="w-8 h-8 object-contain rounded bg-white" src={logo} />
              </div>
            )}
            <div className="flex flex-col gap-px min-w-0">
              {ticker && <span className="font-bold text-gray-900 text-[13px]">{ticker}</span>}
              <span className="text-gray-500 text-[11px] font-normal whitespace-nowrap overflow-hidden text-ellipsis max-w-[120px]">
                {company.name}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default [
  {
    cellClass: 'fda-cell',
    cellRenderer: CompanyCell,
    field: 'name',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    headerName: 'Company',
    initialWidth: 200,
    keyCreator: function (params: KeyCreatorParams) {
      return params.value?.map?.((company: { name: string }) => company.name) || [];
    },
    minWidth: 160,
    valueGetter: function (params) {
      return params.data['companies'];
    },
    ...headerInfoTooltipDef('Company developing the drug'),
  },
  {
    autoHeight: true,
    cellClass: 'wrap-cell',
    cellRenderer: ({ data }: { data: FDA }) => {
      if (!data?.drug?.name) return '';
      return (
        <>
          <b>{data.drug.name}</b>
          <br />
          <span>{data.drug.indication_symptom.join(', ')}</span>
        </>
      );
    },
    field: 'drug',
    headerName: 'Drug & Treatment',
    initialWidth: 180,
    minWidth: 180,
    suppressHeaderMenuButton: true,
    ...headerInfoTooltipDef('Drug name and symptoms/diseases it is used to treat'),
  },
  {
    autoHeight: true,
    cellClass: 'wrap-cell',
    field: 'status',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    headerName: 'Status',
    initialWidth: 100,
    minWidth: 90,
    ...headerInfoTooltipDef('Current status in testing'),
  },
  {
    autoHeight: true,
    cellClass: 'wrap-cell',
    field: 'quote.last',
    headerName: 'Price',
    initialWidth: 60,
    minWidth: 60,
    suppressHeaderMenuButton: true,
    valueFormatter: ({ data }) => formatLongPrice(data?.quote?.last),
    ...headerInfoTooltipDef('Current Stock Price'),
  },
  {
    autoHeight: true,
    cellClass: 'wrap-cell',
    field: 'quote.changePercent',
    headerName: '% change',
    initialWidth: 60,
    minWidth: 60,
    suppressHeaderMenuButton: true,
    valueFormatter: ({ data }) => formatPercentage(data?.quote?.changePercent),
    ...headerInfoTooltipDef('% Change in Price from Open'),
  },
  {
    cellClass: 'ticker-cell',
    comparator: (valueA, valueB) => {
      // "target_date": "string (YYYY-MM, YYYY-Q[1-4], YYYY-H[1-2], or YYYY-[EARLY, MID, LATE])",
      const sortBy = [
        '01',
        '02',
        '03',
        'EARLY',
        'Q1',
        '04',
        '05',
        '06',
        'MID',
        'Q2',
        'H1',
        '07',
        '08',
        '09',
        'Q3',
        '10',
        '11',
        '12',
        'LATE',
        'Q4',
        'H2',
      ];

      try {
        const formattedDateA = valueA.toString().split('-');
        const formattedDateB = valueB.toString().split('-');

        const periodA = formattedDateA;
        const periodB = formattedDateB;
        let [yearA] = formattedDateA;
        let [yearB] = formattedDateB;

        yearA = parseInt(yearA);
        yearB = parseInt(yearB);

        if (isNaN(yearA) || isNaN(yearB)) return 0;

        if (yearA > yearB) return 1;
        if (yearA < yearB) return -1;

        const periodAIndex = sortBy.indexOf(periodA.toString());
        const periodBIndex = sortBy.indexOf(periodB.toString());
        if (periodAIndex === periodBIndex) return 0;
        return periodAIndex > periodBIndex ? 1 : -1;
      } catch (error) {
        console.error('estimated_date comparator err', error);
        return 0;
      }
    },
    field: 'estimated_date',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
    },
    headerName: 'Catalyst Date',
    initialWidth: 100,
    minWidth: 100,
    valueGetter: ({ data }) => data.target_date || '',
    ...headerInfoTooltipDef('Catalyst Date'),
  },
  {
    cellClass: 'ticker-cell',
    field: 'exact_date',
    filter: 'agDateColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      ...dateFilterParams,
      buttons: ['clear'],
      inRangeInclusive: true,
    },
    headerName: 'Catalyst Date',
    initialWidth: 100,
    minWidth: 100,
    valueFormatter: ({ data }) => data.target_date,
    valueGetter: ({ data }) => data.target_date || null,
    ...headerInfoTooltipDef('Catalyst Date'),
  },
  {
    cellClass: 'ticker-cell',
    cellRenderer: ({ value }: { value: string | number | Date }) => {
      if (!value) return '';
      const dateTime = DateTime.fromJSDate(value as Date);
      return (
        <>
          {dateTime.toFormat('MM/dd/yyyy')} <br /> {dateTime.toFormat('h:mm a')}
        </>
      );
    },
    field: 'historical_date',
    filter: 'agDateColumnFilter',
    headerName: 'Updated',
    initialSort: 'desc' as ColDef['initialSort'],
    initialWidth: 140,
    minWidth: 120,
    valueGetter: ({ data }) => {
      if (!data.time || !data.date) return '';
      const date_time = `${data.date}T${data.time}`;
      return DateTime.fromISO(date_time).toJSDate();
    },
    ...headerInfoTooltipDef('Date and time of announcement from FDA'),
  },
  {
    autoHeight: true,
    cellClass: 'wrap-cell',
    field: 'event_type',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    headerName: 'Catalyst',
    initialWidth: 140,
    minWidth: 130,
    ...headerInfoTooltipDef('Catalyst of event being reported'),
  },
  {
    autoHeight: true,
    cellClass: 'wrap-cell',
    cellRenderer: ({ data }: { data: FDA }) => {
      if (!data?.outcome) return '';
      return (
        <>
          <p>{data.outcome}</p>
          {data.source_type && data.source_link && (
            <a href={data.source_link} rel="noreferrer" target="_blank">
              View Source →
            </a>
          )}
        </>
      );
    },
    field: 'outcome',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    headerName: 'Description',
    initialWidth: 450,
    maxWidth: 500,
    suppressHeaderMenuButton: true,
    ...headerInfoTooltipDef('Description of Event'),
  },
  {
    cellClass: 'ticker-cell',
    cellRenderer: () => {
      return (
        <a
          href="https://www.benzinga.com/profile/portfolio/?action=login&utm_source=calendars"
          rel="noreferrer"
          target="_blank"
        >
          Get Alert
        </a>
      );
    },
    field: 'get_alert',
    headerName: 'Get Alert',
    initialWidth: 70,
    minWidth: 70,
    suppressHeaderMenuButton: true,
  },
] as (ColDef | ColGroupDef)[];
